import AppsHeader from "@lib/components/AppComponents/AppsContainer/AppsHeader";
import AppTableContentHeader from "@lib/components/MiniComponents/Table/AppTableContentHeader";
import AppsContent from "@lib/components/AppComponents/AppsContainer/AppsContent";
import { TableHeaderFilter } from "@lib/types/Api";
import { useMemo } from "react";
import { useParamsManager } from "@lib/hooks/useParamsManager";
import { handleTableChange } from "@lib/helpers/UrlHelper";
import { TableProps } from "antd";
import { useIntl } from "react-intl";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import AppsContainer from "@lib/components/AppComponents/AppsContainer";
import AppTable from "@lib/components/MiniComponents/Table/AppTable";
import { AppContext, SharedContextProps } from "@lib/types";
import { useQuery } from "@tanstack/react-query";
import { apiGetCustomerLeadScannedTransaction } from "@lib/api/walletSendByte";
import useScreen from "@lib/hooks/useScreen";
import getColumns from "./columns";
import _ from "lodash";
import { SendByteScannedTransaction } from "@lib/types/SendByte";

const SharedSendBytePaymentList: React.FC<SharedContextProps> = ({
  context,
  customerId,
  leadId,
  customTitle,
  withoutContainer = false,
  pageIsActive = true,
  tableProps,
  height,
  minusHeight,
  queryParams,
}) => {
  const { messages } = useIntl();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { type = context || AppContext.CUSTOMER } = useParams();
  const { isMobile } = useScreen();

  // Context-specific query parameters
  const initialFilters = [
    ...(state?.sendByteWalletAddress
      ? [`sendByteWalletAddress.address||$eq||${state?.sendByteWalletAddress}`]
      : []),
    ...(customerId ? [`customerId||$eq||${customerId}`] : []),
    ...(leadId ? [`sendByteWalletAddress.customerUserId||$eq||${leadId}`] : []),
  ];

  const paramsManagerProps = useMemo(() => {
    return queryParams
      ? queryParams
      : {
          page: 1,
          limit: 100,
          sort: "confirmedDate,DESC",
          join: ["customerUser", "customer", "sendByteWalletAddress"],
          filter: initialFilters,
        };
  }, [queryParams, initialFilters]);

  const { params, setParams, handleRemoveFilters } = useParamsManager({
    ...paramsManagerProps,
    isShowParamsInUrl: false,
  });

  const query = useQuery({
    queryFn: () => apiGetCustomerLeadScannedTransaction(params),
    queryKey: ["sendbyte-payments", JSON.stringify(params)],
    refetchInterval: 1000 * 60,
    enabled: !!params && pageIsActive,
  });
  const { data, isLoading, isFetching } = query;
  const apiData = data?.data?.data;

  const columns = useMemo(() => {
    return getColumns({
      context: type as AppContext,
      apiData: apiData as SendByteScannedTransaction[],
      params,
      messages,
      navigate,
      columnsConfig: tableProps?.columnsConfig,
      isMobile,
    });
  }, [apiData, params, type]);

  const onPageChange = (page: number, limit: number) => {
    if (page !== params.page || limit !== params.limit) {
      setParams({ page, limit });
    }
  };

  const onChange: TableProps<any>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    handleTableChange({ pagination, filters, sorter, params, setParams });
  };

  const filters = useMemo(() => {
    if (!params?.filter) return [];

    return params?.filter
      .map((f) => ({
        key: f.split("||")[0],
        title: columns.find((c) => c?.key === f.split("||")[0])?.title || "",
        operator: f.split("||")[1],
        value: f.split("||")[2],
      }))
      .filter(
        (f) =>
          !["customerId", "sendByteWalletAddress.customerUserId"].includes(
            f.key,
          ),
      ) as TableHeaderFilter[];
  }, [params, state]);

  // Context-specific title and breadcrumb
  const title = customTitle || (messages["common.sendBytePayments"] as string);

  return (
    <AppsContainer
      fullView={true}
      title={title}
      noContainer={withoutContainer}
      // height={height}
      cardProps={{
        borderFull: true,
        coloredHeader: true,
      }}
    >
      <AppsHeader>
        <AppTableContentHeader
          showInfo
          page={params?.page || 1}
          perPage={params?.limit || 10}
          onPageChange={onPageChange}
          query={query}
          filters={filters}
          handleRemoveFilters={handleRemoveFilters}
          tableProps={tableProps}
        />
      </AppsHeader>
      <AppsContent>
        <AppTable
          dataSource={apiData}
          columns={columns}
          rowKey={"id"}
          loading={isLoading || isFetching}
          onChange={onChange}
          scroll={{ y: height }}
          minusHeight={minusHeight}
          {...tableProps}
        />
      </AppsContent>
    </AppsContainer>
  );
};

export default SharedSendBytePaymentList;
